import React from "react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

/**
 * 🆕 22/07/2026 — Menu rapido WhatsApp con template messaggi pre-compilati.
 * Cliccando il pulsante WhatsApp appare un dropdown con template comuni + opzione libera.
 * I placeholder {nome} vengono sostituiti con il nome del cliente.
 *
 * Props:
 *  - number: string (numero grezzo, con o senza spazi/prefisso)
 *  - clientName: string (nome del cliente per personalizzare i template)
 *  - testid: string (opzionale) prefisso data-testid
 */

const normalizeWhatsAppNumber = (raw) => {
  if (!raw) return "";
  const n = String(raw).replace(/[^0-9+]/g, "");
  // 🆕 28/07/2026 — Normalizza in formato internazionale (solo cifre, prefisso paese, senza "+"):
  //  - "003393291234567" → "3393291234567" (rimuove "00", già internazionale)
  //  - "+393291234567"   → "393291234567"  (rimuove "+")
  //  - "393291234567"    → "393291234567"  (già in formato intl IT, invariato)
  //  - "3291234567"      → "393291234567"  (numero locale IT senza prefisso → aggiunge "39")
  if (n.startsWith("00")) return n.slice(2);
  if (n.startsWith("+")) return n.slice(1);
  if (n.length >= 12 && n.startsWith("39") && (n[2] === "3" || n[2] === "0")) return n;
  return "39" + n; // Numero locale italiano
};

const TEMPLATES = [
  {
    id: "scadenza",
    label: "⏰ Ricordo scadenza",
    text: "Ciao {nome}, ti ricordo che la tua polizza è in prossima scadenza. Ti serve una mano per il rinnovo? — Assicura",
  },
  {
    id: "appuntamento",
    label: "📅 Conferma appuntamento",
    text: "Ciao {nome}, ti confermo il nostro appuntamento in agenzia. A presto! — Assicura",
  },
  {
    id: "documenti",
    label: "📄 Documenti pronti",
    text: "Ciao {nome}, i documenti che aspettavi sono pronti. Puoi passare in agenzia quando preferisci. — Assicura",
  },
  {
    id: "pagamento",
    label: "💳 Link pagamento",
    text: "Ciao {nome}, ti invio il link per pagare la tua polizza direttamente online. Se hai dubbi contattami. — Assicura",
  },
  {
    id: "compleanno",
    label: "🎂 Buon compleanno",
    text: "Tantissimi auguri di buon compleanno {nome}! Un caro saluto dal team Assicura.",
  },
];

const buildLink = (num, text) => {
  const n = normalizeWhatsAppNumber(num);
  if (!n) return "";
  const t = (text || "").trim();
  return t ? `whatsapp://send?phone=${n}&text=${encodeURIComponent(t)}` : `whatsapp://send?phone=${n}`;
};

export function WhatsAppQuickMenu({ number, clientName, testid = "wa" }) {
  const applyPlaceholders = (raw) => (raw || "").replace(/\{nome\}/g, (clientName || "").split(" ")[0] || "");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={`${testid}-btn`}
          title="Apri chat WhatsApp"
          className="inline-flex items-center justify-center h-9 w-9 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white transition-colors shrink-0"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 2.1.55 4.15 1.6 5.95L2 22l4.28-1.12c1.75.96 3.72 1.46 5.72 1.46h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.13-2.9-7C17.15 3.06 14.67 2 12.04 2zm0 18.14c-1.75 0-3.44-.47-4.91-1.35l-.35-.21-3.52.92.94-3.43-.23-.36c-.97-1.55-1.48-3.33-1.48-5.15 0-5.36 4.36-9.72 9.73-9.72 2.6 0 5.05 1.01 6.89 2.85 1.84 1.84 2.85 4.29 2.85 6.89-.01 5.36-4.37 9.72-9.72 9.72zm5.34-7.28c-.29-.15-1.73-.85-2-.95-.27-.1-.47-.15-.66.15-.2.29-.76.95-.93 1.15-.17.19-.34.22-.63.07-.29-.15-1.23-.45-2.34-1.44-.86-.77-1.44-1.72-1.62-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.34.44-.51.15-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.07-.15-.66-1.58-.9-2.16-.24-.58-.48-.5-.66-.51-.17-.01-.36-.01-.55-.01-.19 0-.51.07-.77.36-.27.29-1.01.99-1.01 2.41 0 1.42 1.03 2.79 1.18 2.99.15.19 2.03 3.1 4.92 4.35.69.3 1.22.48 1.64.61.69.22 1.31.19 1.81.11.55-.08 1.73-.71 1.97-1.39.24-.68.24-1.27.17-1.39-.07-.12-.27-.19-.56-.34z"/></svg>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs text-emerald-700">💬 Invia messaggio</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {TEMPLATES.map((t) => (
          <DropdownMenuItem
            key={t.id}
            data-testid={`${testid}-tmpl-${t.id}`}
            onSelect={() => {
              const link = buildLink(number, applyPlaceholders(t.text));
              if (link) window.open(link, "_blank", "noopener,noreferrer");
            }}
            className="cursor-pointer flex flex-col items-start gap-0.5 py-2"
          >
            <span className="text-sm font-medium">{t.label}</span>
            <span className="text-[10px] text-slate-500 line-clamp-2">
              {applyPlaceholders(t.text)}
            </span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid={`${testid}-tmpl-free`}
          onSelect={() => {
            const link = buildLink(number, "");
            if (link) window.open(link, "_blank", "noopener,noreferrer");
          }}
          className="cursor-pointer text-sm text-slate-600"
        >
          ✍️ Messaggio libero (senza template)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
